import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Button, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Grid, Select, MenuItem,
  FormControl, InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Box, Typography, CircularProgress, Tooltip,
  Divider
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
  Visibility as ViewIcon,
  Edit as EditIcon
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import { AuthContext } from "../../context/Auth/AuthContext";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  formControl: {
    minWidth: 200,
  },
  statusApproved: { color: "#4caf50" },
  statusPending: { color: "#ff9800" },
  statusRejected: { color: "#f44336" },
  componentBox: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  previewBox: {
    backgroundColor: "#e5ddd5",
    borderRadius: 8,
    padding: theme.spacing(2),
    maxWidth: 400,
  },
  previewBubble: {
    backgroundColor: "#dcf8c6",
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  }
}));

const CATEGORIES = [
  { value: "MARKETING", labelKey: "templateManager.categories.marketing" },
  { value: "UTILITY", labelKey: "templateManager.categories.utility" },
  { value: "AUTHENTICATION", labelKey: "templateManager.categories.authentication" },
];

const LANGUAGES = [
  { value: "pt_BR", label: "Português (BR)" },
  { value: "en_US", label: "English (US)" },
  { value: "es", label: "Español" },
];

const TemplateManager = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState(null);

  // Form state
  const [templateName, setTemplateName] = useState("");
  const [templateCategory, setTemplateCategory] = useState("MARKETING");
  const [templateLanguage, setTemplateLanguage] = useState("pt_BR");
  const [headerText, setHeaderText] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [footerText, setFooterText] = useState("");
  const [buttons, setButtons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [bodyExamples, setBodyExamples] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Detectar variables {{n}} en el cuerpo
  const bodyVariables = React.useMemo(() => {
    const matches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    return [...new Set(matches)]
      .map(v => v.replace(/\{\{|\}\}/g, ""))
      .sort((a, b) => Number(a) - Number(b));
  }, [bodyText]);

  // Texto del preview con variables reemplazadas por ejemplos
  const previewBody = bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) =>
    bodyExamples[n] ? bodyExamples[n] : `{{${n}}}`
  );

  // Renderiza markdown de WhatsApp: *bold*, _italic_, ~strike~, `mono`
  const renderWhatsAppMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    return lines.map((line, li) => {
      const parts = [];
      // Tokenize: split by bold(*), italic(_), strike(~), mono(`)
      const regex = /(\*[^*]+\*|_[^_]+_|~[^~]+~|`[^`]+`)/g;
      let last = 0;
      let match;
      let key = 0;
      while ((match = regex.exec(line)) !== null) {
        if (match.index > last) parts.push(<span key={key++}>{line.slice(last, match.index)}</span>);
        const raw = match[0];
        const inner = raw.slice(1, -1);
        if (raw[0] === "*") parts.push(<strong key={key++}>{inner}</strong>);
        else if (raw[0] === "_") parts.push(<em key={key++}>{inner}</em>);
        else if (raw[0] === "~") parts.push(<del key={key++}>{inner}</del>);
        else parts.push(<code key={key++} style={{ fontFamily: "monospace", background: "#f0f0f0", padding: "0 2px", borderRadius: 2 }}>{inner}</code>);
        last = match.index + raw.length;
      }
      if (last < line.length) parts.push(<span key={key++}>{line.slice(last)}</span>);
      return <React.Fragment key={li}>{parts}{li < lines.length - 1 && <br />}</React.Fragment>;
    });
  };

  // Fetch official whatsapp connections
  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp/?session=0");
        const oficial = data.filter(w => w.channel === "whatsapp_oficial");
        setWhatsapps(oficial);
        if (oficial.length > 0 && !selectedWhatsappId) {
          setSelectedWhatsappId(oficial[0].id);
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchWhatsapps();
  }, []);

  // Fetch templates when whatsapp changes
  const fetchTemplates = useCallback(async () => {
    if (!selectedWhatsappId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/quick-messages/list", {
        params: {
          companyId: user.companyId,
          isOficial: "true",
          whatsappId: selectedWhatsappId
        }
      });
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [selectedWhatsappId, user.companyId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Listen for template status change notifications via socket
  useEffect(() => {
    if (!user?.companyId) return;
    
    // Import socket from context if available
    const handleStatusChange = (data) => {
      if (data.action === "statusChange" && data.changes) {
        data.changes.forEach(change => {
          const statusLabel = {
            APPROVED: `✅ ${i18n.t("templateManager.status.approved")}`,
            REJECTED: `❌ ${i18n.t("templateManager.status.rejected")}`,
            PENDING: `⏳ ${i18n.t("templateManager.status.pending")}`,
            PAUSED: `⏸️ ${i18n.t("templateManager.status.paused")}`,
          };
          const newLabel = statusLabel[change.newStatus] || change.newStatus;
          const oldLabel = statusLabel[change.oldStatus] || change.oldStatus;

          if (change.newStatus === "APPROVED") {
            toast.success(i18n.t("templateManager.toast.approved", { name: change.name }), { autoClose: 8000 });
          } else if (change.newStatus === "REJECTED") {
            toast.error(i18n.t("templateManager.toast.rejected", { name: change.name }), { autoClose: 10000 });
          } else {
            toast.info(i18n.t("templateManager.toast.statusChange", { name: change.name, old: oldLabel, new: newLabel }), { autoClose: 6000 });
          }
        });
        // Refresh templates list
        fetchTemplates();
      }
    };

    // We'll handle notifications from the sync response instead
    return () => {};
  }, [user?.companyId, fetchTemplates]);

  const handleSync = async () => {
    if (!selectedWhatsappId) return;
    setSyncing(true);
    try {
      const { data } = await api.get(`/whatsapp/sync-templates/${selectedWhatsappId}`);
      
      // Show status change notifications
      if (data.statusChanges && data.statusChanges.length > 0) {
        data.statusChanges.forEach(change => {
          if (change.newStatus === "APPROVED") {
            toast.success(i18n.t("templateManager.toast.approved", { name: change.name }), { autoClose: 8000 });
          } else if (change.newStatus === "REJECTED") {
            toast.error(i18n.t("templateManager.toast.rejected", { name: change.name }), { autoClose: 10000 });
          } else {
            toast.info(i18n.t("templateManager.toast.statusChange", { name: change.name, old: change.oldStatus, new: change.newStatus }), { autoClose: 6000 });
          }
        });
        toast.success(i18n.t("templateManager.toast.syncDone", { count: data.statusChanges.length }));
      } else {
        toast.success(i18n.t("templateManager.toast.syncNoChanges"));
      }
      
      await fetchTemplates();
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "";
      if (errMsg.includes("TOKEN_EXPIRED") || errMsg.includes("token") && (errMsg.includes("expirou") || errMsg.includes("inválido"))) {
        toast.error(i18n.t("templateManager.toast.tokenExpired"), { autoClose: 12000 });
      } else {
        toastError(err);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateTemplate = async () => {
    setSubmitAttempted(true);

    if (!templateName || !bodyText) {
      toast.error(i18n.t("templateManager.validation.nameAndBodyRequired"));
      return;
    }

    if (bodyVariables.length > 0) {
      const missing = bodyVariables.filter(v => !bodyExamples[v]?.trim());
      if (missing.length > 0) {
        const varNames = missing.map(v => `{{${v}}}`).join(", ");
        toast.error(`${i18n.t("templateManager.validation.examplesRequired")} ${varNames}`);
        return;
      }
    }

    // Validate template name (only lowercase, numbers and underscores)
    const nameRegex = /^[a-z0-9_]+$/;
    if (!nameRegex.test(templateName)) {
      toast.error("O nome do template deve conter apenas letras minúsculas, números e underscores (_)");
      return;
    }

    setSubmitting(true);
    try {
      const components = [];

      if (headerText) {
        components.push({ type: "HEADER", format: "TEXT", text: headerText });
      }

      const bodyComponent = { type: "BODY", text: bodyText };
      if (bodyVariables.length > 0) {
        const exampleValues = bodyVariables.map(v => bodyExamples[v]);
        bodyComponent.example = { body_text: [exampleValues] };
      }
      components.push(bodyComponent);

      if (footerText) {
        components.push({ type: "FOOTER", text: footerText });
      }

      if (buttons.length > 0) {
        components.push({
          type: "BUTTONS",
          buttons: buttons.map(b => ({
            type: b.type,
            text: b.text,
            ...(b.type === "URL" ? { url: b.url } : {}),
            ...(b.type === "PHONE_NUMBER" ? { phone_number: b.phone } : {})
          }))
        });
      }

      await api.post(`/whatsapp/${selectedWhatsappId}/create-template`, {
        name: templateName,
        language: templateLanguage,
        category: templateCategory,
        components
      });

      toast.success(i18n.t("templateManager.toast.submitted"));
      setCreateModalOpen(false);
      resetForm();

      // Sync to get latest status
      await handleSync();
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "";
      if (errMsg.includes("TOKEN_EXPIRED") || (errMsg.includes("token") && (errMsg.includes("expirou") || errMsg.includes("inválido")))) {
        toast.error(i18n.t("templateManager.toast.tokenExpired"), { autoClose: 12000 });
      } else {
        toastError(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deletingTemplate) return;
    try {
      await api.delete(`/whatsapp/${selectedWhatsappId}/delete-template/${deletingTemplate.shortcode}`);
      toast.success(i18n.t("templateManager.toast.deleted"));
      setConfirmModalOpen(false);
      setDeletingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "";
      if (errMsg.includes("TOKEN_EXPIRED") || (errMsg.includes("token") && (errMsg.includes("expirou") || errMsg.includes("inválido")))) {
        toast.error(i18n.t("templateManager.toast.tokenExpired"), { autoClose: 12000 });
      } else {
        toastError(err);
      }
    }
  };

  const handleEditTemplate = (template) => {
    // Pre-fill the create form with existing template data for re-creation
    setTemplateName(template.shortcode || "");
    setTemplateCategory(template.category || "MARKETING");
    setTemplateLanguage(template.language || "pt_BR");
    
    // Parse components if available
    if (template.components && template.components.length > 0) {
      const header = template.components.find(c => c.type === "HEADER");
      const body = template.components.find(c => c.type === "BODY");
      const footer = template.components.find(c => c.type === "FOOTER");
      const btns = template.components.find(c => c.type === "BUTTONS");

      setHeaderText(header?.text || "");
      setBodyText(body?.text || "");
      setFooterText(footer?.text || "");

      if (btns?.buttons) {
        const parsed = typeof btns.buttons === "string" ? JSON.parse(btns.buttons) : btns.buttons;
        setButtons((parsed || []).map(b => ({
          type: b.type || "QUICK_REPLY",
          text: b.text || "",
          url: b.url || "",
          phone: b.phone_number || ""
        })));
      } else {
        setButtons([]);
      }
    } else {
      setHeaderText("");
      setBodyText(template.message || "");
      setFooterText("");
      setButtons([]);
    }

    setCreateModalOpen(true);
  };

  const resetForm = () => {
    setTemplateName("");
    setTemplateCategory("MARKETING");
    setTemplateLanguage("pt_BR");
    setHeaderText("");
    setBodyText("");
    setFooterText("");
    setButtons([]);
    setBodyExamples({});
    setSubmitAttempted(false);
  };

  const addButton = (type) => {
    if (buttons.length >= 3) {
      toast.warning("Máximo de 3 botões permitidos");
      return;
    }
    setButtons([...buttons, { type, text: "", url: "", phone: "" }]);
  };

  const updateButton = (index, field, value) => {
    const updated = [...buttons];
    updated[index][field] = value;
    setButtons(updated);
  };

  const removeButton = (index) => {
    setButtons(buttons.filter((_, i) => i !== index));
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return <Chip icon={<CheckCircleIcon />} label={i18n.t("templateManager.status.approved")} size="small" style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }} />;
      case "PENDING":
        return <Chip icon={<PendingIcon />} label={i18n.t("templateManager.status.pending")} size="small" style={{ backgroundColor: "#fff3e0", color: "#e65100" }} />;
      case "REJECTED":
        return <Chip icon={<RejectedIcon />} label={i18n.t("templateManager.status.rejected")} size="small" style={{ backgroundColor: "#ffebee", color: "#c62828" }} />;
      default:
        return <Chip label={status || "N/A"} size="small" />;
    }
  };

  const getCategoryLabel = (cat) => {
    const found = CATEGORIES.find(c => c.value === cat);
    return found ? i18n.t(found.labelKey) : cat;
  };

  const filteredTemplates = templates.filter(t =>
    !searchParam || t.shortcode?.toLowerCase().includes(searchParam.toLowerCase())
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={`${i18n.t("templateManager.deleteModal.title")} "${deletingTemplate?.shortcode}"?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteTemplate}
      >
        {i18n.t("templateManager.deleteModal.body")}
      </ConfirmationModal>

      {/* View Modal */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{i18n.t("templateManager.viewModal.title")}</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">{i18n.t("templateManager.viewModal.name")}</Typography>
                  <Typography>{selectedTemplate.shortcode}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">{i18n.t("templateManager.viewModal.language")}</Typography>
                  <Typography>{selectedTemplate.language}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="subtitle2" color="textSecondary">{i18n.t("templateManager.viewModal.status")}</Typography>
                  {getStatusIcon(selectedTemplate.status)}
                </Grid>
              </Grid>
              <Box mt={2}>
                <Typography variant="subtitle2" color="textSecondary">{i18n.t("templateManager.viewModal.category")}</Typography>
                <Typography>{getCategoryLabel(selectedTemplate.category)}</Typography>
              </Box>
              {selectedTemplate.components && selectedTemplate.components.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>{i18n.t("templateManager.viewModal.components")}</Typography>
                  <Box className={classes.previewBox}>
                    {selectedTemplate.components.map((comp, i) => (
                      <Box key={i} mb={1}>
                        {comp.type === "HEADER" && (
                          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>{comp.text}</Typography>
                        )}
                        {comp.type === "BODY" && (
                          <Box className={classes.previewBubble}>
                            <Typography style={{ whiteSpace: "pre-wrap" }}>{comp.text}</Typography>
                          </Box>
                        )}
                        {comp.type === "FOOTER" && (
                          <Typography variant="caption" color="textSecondary">{comp.text}</Typography>
                        )}
                        {comp.type === "BUTTONS" && comp.buttons && (
                          <Box mt={1}>
                            {(typeof comp.buttons === "string" ? JSON.parse(comp.buttons) : comp.buttons).map((btn, j) => (
                              <Button key={j} variant="outlined" size="small" fullWidth style={{ marginBottom: 4 }}>
                                {btn.text}
                              </Button>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewModalOpen(false)} color="primary">{i18n.t("templateManager.viewModal.close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Create Modal */}
      <Dialog open={createModalOpen} onClose={() => { setCreateModalOpen(false); resetForm(); }} maxWidth="lg" fullWidth>
        <DialogTitle>{i18n.t("templateManager.createModal.title")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>

            {/* ── Columna izquierda: formulario ── */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label={i18n.t("templateManager.createModal.templateName")}
                    fullWidth margin="dense" variant="outlined"
                    value={templateName}
                    onChange={e => setTemplateName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                    helperText={i18n.t("templateManager.createModal.templateNameHelper")}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth margin="dense" variant="outlined">
                    <InputLabel>{i18n.t("templateManager.createModal.category")}</InputLabel>
                    <Select value={templateCategory} onChange={e => setTemplateCategory(e.target.value)} label={i18n.t("templateManager.createModal.category")}>
                      {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{i18n.t(c.labelKey)}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth margin="dense" variant="outlined">
                    <InputLabel>{i18n.t("templateManager.createModal.language")}</InputLabel>
                    <Select value={templateLanguage} onChange={e => setTemplateLanguage(e.target.value)} label={i18n.t("templateManager.createModal.language")}>
                      {LANGUAGES.map(l => <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider style={{ margin: "12px 0" }} />

              <TextField
                label={i18n.t("templateManager.createModal.header")}
                fullWidth margin="dense" variant="outlined"
                value={headerText}
                onChange={e => setHeaderText(e.target.value)}
                helperText={i18n.t("templateManager.createModal.headerHelper")}
              />

              <TextField
                label={i18n.t("templateManager.createModal.body")}
                fullWidth margin="dense" variant="outlined"
                multiline rows={4}
                value={bodyText}
                onChange={e => setBodyText(e.target.value)}
                helperText={i18n.t("templateManager.createModal.bodyHelper").replace(/\{(\d+)\}/g, "{{$1}}")}
                required
              />

              {bodyVariables.length > 0 && (
                <Box mt={1} mb={1} p={2} style={{ background: "rgba(59,130,246,0.06)", borderRadius: 8, border: "1px solid rgba(59,130,246,0.2)" }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {i18n.t("templateManager.createModal.examplesTitle")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    {i18n.t("templateManager.createModal.examplesSubtitle")}
                  </Typography>
                  <Grid container spacing={1}>
                    {bodyVariables.map(v => (
                      <Grid item xs={12} sm={6} key={v}>
                        <TextField
                          size="small" fullWidth variant="outlined"
                          label={`${i18n.t("templateManager.createModal.exampleFor")} {{${v}}}`}
                          value={bodyExamples[v] || ""}
                          onChange={e => { const val = e.target.value; setBodyExamples(prev => ({ ...prev, [v]: val })); }}
                          required
                          error={submitAttempted && !bodyExamples[v]?.trim()}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <TextField
                label={i18n.t("templateManager.createModal.footer")}
                fullWidth margin="dense" variant="outlined"
                value={footerText}
                onChange={e => setFooterText(e.target.value)}
                helperText={i18n.t("templateManager.createModal.footerHelper")}
              />

              <Divider style={{ margin: "12px 0" }} />

              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2">{i18n.t("templateManager.createModal.buttonsTitle")}</Typography>
                <Box>
                  <Button size="small" onClick={() => addButton("QUICK_REPLY")} style={{ marginRight: 8 }}>
                    {i18n.t("templateManager.createModal.quickReply")}
                  </Button>
                  <Button size="small" onClick={() => addButton("URL")}>
                    {i18n.t("templateManager.createModal.urlButton")}
                  </Button>
                  <Button size="small" onClick={() => addButton("PHONE_NUMBER")} style={{ marginLeft: 8 }}>
                    {i18n.t("templateManager.createModal.phoneButton")}
                  </Button>
                </Box>
              </Box>

              {buttons.map((btn, i) => (
                <Box key={i} className={classes.componentBox}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                      <Chip label={btn.type === "QUICK_REPLY" ? i18n.t("templateManager.createModal.chipQuickReply") : btn.type === "URL" ? i18n.t("templateManager.createModal.chipUrl") : i18n.t("templateManager.createModal.chipPhone")} size="small" />
                    </Grid>
                    <Grid item xs={btn.type === "QUICK_REPLY" ? 8 : 4}>
                      <TextField size="small" label={i18n.t("templateManager.createModal.textLabel")} fullWidth value={btn.text} onChange={e => updateButton(i, "text", e.target.value)} />
                    </Grid>
                    {btn.type === "URL" && (
                      <Grid item xs={4}>
                        <TextField size="small" label={i18n.t("templateManager.createModal.urlLabel")} fullWidth value={btn.url} onChange={e => updateButton(i, "url", e.target.value)} />
                      </Grid>
                    )}
                    {btn.type === "PHONE_NUMBER" && (
                      <Grid item xs={4}>
                        <TextField size="small" label={i18n.t("templateManager.createModal.phoneLabel")} fullWidth value={btn.phone} onChange={e => updateButton(i, "phone", e.target.value)} />
                      </Grid>
                    )}
                    <Grid item xs={1}>
                      <IconButton size="small" onClick={() => removeButton(i)}><DeleteIcon fontSize="small" /></IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Grid>

            {/* ── Columna derecha: vista previa ── */}
            <Grid item xs={12} md={5}>
              <Box position="sticky" top={0}>
                <Typography variant="subtitle2" gutterBottom>{i18n.t("templateManager.createModal.preview")}</Typography>
                <Box style={{
                  background: "#e5ddd5",
                  borderRadius: 12,
                  padding: 16,
                  minHeight: 200,
                  backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8b8a2' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
                }}>
                  {bodyText ? (
                    <Box style={{
                      background: "#fff",
                      borderRadius: "4px 12px 12px 12px",
                      padding: "10px 12px",
                      maxWidth: "85%",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
                      display: "inline-block",
                      width: "100%",
                    }}>
                      {headerText && (
                        <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 6 }}>{headerText}</Typography>
                      )}
                      <Typography component="div" style={{ fontSize: 14, lineHeight: 1.5 }}>{renderWhatsAppMarkdown(previewBody)}</Typography>
                      {footerText && (
                        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>{footerText}</Typography>
                      )}
                      {buttons.length > 0 && (
                        <Box mt={1} borderTop="1px solid #e0e0e0" pt={1}>
                          {buttons.map((btn, i) => (
                            <Typography key={i} align="center" style={{ color: "#00a5f4", fontSize: 13, padding: "4px 0", borderBottom: i < buttons.length - 1 ? "1px solid #e0e0e0" : "none" }}>
                              {btn.text || i18n.t("templateManager.createModal.buttonPlaceholder", { index: i + 1 })}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="textSecondary" style={{ display: "block", textAlign: "center", marginTop: 60 }}>
                      {i18n.t("templateManager.createModal.previewEmpty")}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCreateModalOpen(false); resetForm(); }}>
            {i18n.t("templateManager.createModal.cancel")}
          </Button>
          <Button
            onClick={handleCreateTemplate}
            color="primary"
            variant="contained"
            disabled={submitting || !templateName || !bodyText}
          >
            {submitting ? <CircularProgress size={24} /> : i18n.t("templateManager.createModal.submit")}
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} sm={5} item>
            <Title>{i18n.t("templateManager.title")}</Title>
          </Grid>
          <Grid xs={12} sm={7} item>
            <Grid spacing={2} container alignItems="center">
              <Grid xs={4} item>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel>{i18n.t("templateManager.connection")}</InputLabel>
                  <Select
                    value={selectedWhatsappId}
                    onChange={e => setSelectedWhatsappId(e.target.value)}
                    label={i18n.t("templateManager.connection")}
                  >
                    {whatsapps.map(w => (
                      <MenuItem key={w.id} value={w.id}>
                        {w.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={3} item>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={i18n.t("templateManager.search")}
                  type="search"
                  value={searchParam}
                  onChange={e => setSearchParam(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: "gray" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid xs={2} item>
                <Tooltip title={i18n.t("templateManager.syncTooltip")}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleSync}
                    disabled={syncing || !selectedWhatsappId}
                    startIcon={syncing ? <CircularProgress size={18} /> : <SyncIcon />}
                  >
                    {i18n.t("templateManager.sync")}
                  </Button>
                </Tooltip>
              </Grid>
              <Grid xs={3} item>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => setCreateModalOpen(true)}
                  disabled={!selectedWhatsappId}
                  startIcon={<AddIcon />}
                >
                  {i18n.t("templateManager.createTemplate")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        {whatsapps.length === 0 && !loading ? (
          <Box p={4} textAlign="center">
            <Typography color="textSecondary">
              {i18n.t("templateManager.noConnection")}
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("templateManager.table.name")}</TableCell>
                <TableCell align="center">{i18n.t("templateManager.table.category")}</TableCell>
                <TableCell align="center">{i18n.t("templateManager.table.language")}</TableCell>
                <TableCell align="center">{i18n.t("templateManager.table.status")}</TableCell>
                <TableCell align="center">{i18n.t("templateManager.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTemplates.map((template) => (
                <TableRow key={template.id}>
                  <TableCell>{template.shortcode}</TableCell>
                  <TableCell align="center">
                    <Chip label={getCategoryLabel(template.category)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">{template.language || "pt_BR"}</TableCell>
                  <TableCell align="center">{getStatusIcon(template.status)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={i18n.t("templateManager.tooltips.view")}>
                      <IconButton size="small" onClick={() => { setSelectedTemplate(template); setViewModalOpen(true); }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("templateManager.tooltips.edit")}>
                      <IconButton size="small" onClick={() => handleEditTemplate(template)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("templateManager.tooltips.delete")}>
                      <IconButton size="small" onClick={() => { setDeletingTemplate(template); setConfirmModalOpen(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={5} />}
              {!loading && filteredTemplates.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="textSecondary" style={{ padding: 16 }}>
                      {i18n.t("templateManager.noTemplates")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default TemplateManager;
